/**
 * <header-search> — the navbar's live search, one instance per surface
 * (`data-surface="desktop"` in the dropdown, `"mobile"` in the drawer).
 *
 * A typed query resolves against two sources and renders as the two groups the
 * design specifies — Products, then Frequency Library:
 *
 *   text query    → the Frequency Library API's `q` param, and Shopify
 *                   predictive search for products.
 *                   When predictive search comes back empty but frequencies
 *                   matched, the Products group falls back to DEVICE rows, so
 *                   "sleep" still leads somewhere buyable.
 *   numeric query → one wide `q` request, narrowed to the Hz windows planned by
 *                   header-search-query.js. Predictive search is NOT called (a
 *                   bare number can never match a product); instead the Products
 *                   group holds DEVICE rows — the devices that can actually run
 *                   what was matched, each citing its frequency ("Runs 7.83 Hz").
 *
 * Each instance owns its input, debounce timer, abort controller and results
 * node; there is no shared mutable state between the two beyond one module-level
 * cache (query results plus the device product data).
 *
 * Interface with the sticky-header controller, which owns the navbar mechanics:
 * this element dispatches `header-search:query` when the query appears or goes
 * away (the drawer swaps its menu for results on that), and exposes reset() /
 * focusInput() for the controller to call when a panel opens or closes. Nothing
 * else couples them.
 *
 * Failures are silent in the UI and loud in the console: an empty panel is
 * better than an error box in the navbar, and a customer report needs something
 * to look at. Legitimately-empty result sets are not logged.
 */

import { pickByWindows, planQuery } from "@theme/header/search-query"

const DEBOUNCE_MS = 250

// Display limits per group. Products is a max of 4 whether it holds product
// hits or device rows; devices top out at 3 because there are three of them.
const MAX_FREQUENCY_ROWS = 5
const MAX_DEVICE_ROWS = 3

// Each frequency's `devices` list (computed by the API) is the sole authority on
// what a device can run — no Hz thresholds are recomputed here. These are the
// API's device ids, which are also the device-handles keys. Cheapest first.
const DEVICE_ORDER = ["v1", "v2", "v3"]

const CARET_RIGHT =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M181.66 133.66l-80 80a8 8 0 0 1-11.32-11.32L164.69 128 90.34 53.66a8 8 0 0 1 11.32-11.32l80 80a8 8 0 0 1 0 11.32Z"/></svg>'

// Same mark as snippets/icon.sparkle.liquid, inlined because this module
// renders its rows in JS. Kept in sync by hand; it is a static icon.
const SPARKLE =
  '<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 256 256" fill="currentColor" aria-hidden="true"><path d="M197.58 129.06L146 110l-19-51.62a15.92 15.92 0 0 0-29.88 0L78 110l-51.62 19a15.92 15.92 0 0 0 0 29.88L78 178l19 51.62a15.92 15.92 0 0 0 29.88 0L146 178l51.62-19a15.92 15.92 0 0 0 0-29.88ZM137 164.22a8 8 0 0 0-4.74 4.74L112 223.85 91.78 169A8 8 0 0 0 87 164.22L32.15 144 87 123.78A8 8 0 0 0 91.78 119L112 64.15 132.22 119a8 8 0 0 0 4.74 4.74L191.85 144ZM144 40a8 8 0 0 1 8-8h16V16a8 8 0 0 1 16 0v16h16a8 8 0 0 1 0 16h-16v16a8 8 0 0 1-16 0V48h-16a8 8 0 0 1-8-8Zm104 48a8 8 0 0 1-8 8h-8v8a8 8 0 0 1-16 0v-8h-8a8 8 0 0 1 0-16h8v-8a8 8 0 0 1 16 0v8h8a8 8 0 0 1 8 8Z"/></svg>'

// ---- Config ---------------------------------------------------------------
// One JSON block, rendered once by sections/header.liquid: the API base URL
// (a theme setting, shared with the Frequency Library page), the three device
// handles (from the device-handles snippet), the curated subtitle map, and the
// idle rows. Read lazily so a header without the block degrades to nothing.

let CONFIG = null

function config() {
  if (CONFIG) return CONFIG

  CONFIG = {
    apiBase: "",
    libraryUrl: "/pages/frequencies",
    schummiUrl: "/pages/schummi-ai",
    deviceHandles: {},
    subtitles: {},
    idle: { products: [], frequencies: [] },
  }

  const node = document.querySelector("[data-header-search-config]")
  if (!node) {
    console.warn("header-search: no config block found")
    return CONFIG
  }

  try {
    const parsed = JSON.parse(node.textContent)
    CONFIG.apiBase = String(parsed.apiBase || "").replace(/\/+$/, "")
    CONFIG.libraryUrl = parsed.libraryUrl || CONFIG.libraryUrl
    CONFIG.schummiUrl = parsed.schummiUrl || CONFIG.schummiUrl
    CONFIG.deviceHandles = parsed.deviceHandles || {}
    CONFIG.subtitles = parsed.subtitles || {}
    CONFIG.idle = {
      products: (parsed.idle && parsed.idle.products) || [],
      frequencies: (parsed.idle && parsed.idle.frequencies) || [],
    }
  } catch (error) {
    console.warn("header-search: could not parse the config block", error)
  }

  return CONFIG
}

function routeRoot() {
  const root =
    (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) ||
    "/"
  return root.endsWith("/") ? root : root + "/"
}

// ---- Cache ----------------------------------------------------------------
// Shared by both instances, so backspacing, retyping, or switching between the
// drawer and the dropdown in one session costs nothing. Only fully successful
// fetches land here — a partial failure must not be remembered as the answer.

const RESULT_CACHE = new Map()
let DEVICE_PRODUCTS = null

// ---- Fetching -------------------------------------------------------------

function isAbort(error) {
  return error && (error.name === "AbortError" || error.name === "TimeoutError")
}

async function fetchJson(url, signal) {
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
    signal,
  })
  if (!response.ok) throw new Error(`HTTP ${response.status} for ${url}`)
  return response.json()
}

/**
 * Run the plan's one frequency request. A numeric request's response is
 * filtered and ordered WINDOW BY WINDOW, tightest first (pickByWindows). Text
 * results keep the API's own order — there is no client-side re-ranking.
 */
async function fetchFrequencies(request, signal) {
  const base = config().apiBase
  if (!base)
    throw new Error("header-search: no frequency API base URL configured")

  const params = new URLSearchParams({ pageSize: String(request.pageSize) })
  if (request.q) params.set("q", request.q)
  const page = await fetchJson(`${base}/frequencies?${params}`, signal)

  const items = page && page.data
  if (!Array.isArray(items))
    throw new Error("header-search: unexpected /frequencies response")

  // A numeric `q` is a substring superset; the windows keep the prefix matches.
  const picked = request.windows
    ? pickByWindows(items, request.windows, MAX_FREQUENCY_ROWS)
    : items

  const rows = []
  picked.forEach((item) => {
    if (!item || !item.id) return
    const hz = parseFloat(item.valueHz)
    if (!isFinite(hz)) return
    rows.push({
      id: item.id,
      hz,
      name: item.name || "",
      devices: Array.isArray(item.devices)
        ? item.devices.map((device) => device && device.id)
        : null,
    })
  })

  return rows.slice(0, MAX_FREQUENCY_ROWS)
}

/** Shopify predictive search, product resource only. */
async function fetchProducts(request, signal) {
  const params = new URLSearchParams({
    q: request.q,
    "resources[type]": "product",
    "resources[limit]": String(request.limit),
    "resources[options][unavailable_products]": "last",
  })

  const data = await fetchJson(
    `${routeRoot()}search/suggest.json?${params}`,
    signal
  )
  const items =
    data &&
    data.resources &&
    data.resources.results &&
    data.resources.results.products
  if (!Array.isArray(items))
    throw new Error("header-search: unexpected predictive-search response")

  return items.slice(0, request.limit).map((product) => ({
    title: product.title || "",
    url: product.url || "",
    image: thumbUrl(
      (product.featured_image && product.featured_image.url) || product.image
    ),
    // Curated copy where we have it; the price is a reasonable stand-in where
    // we don't, and it beats an empty second line.
    sub: config().subtitles[product.handle] || formatPrice(product.price),
  }))
}

/**
 * The three device products, fetched once and kept for the page. Numeric
 * queries need their real titles and thumbnails; nothing else does, so this
 * stays lazy. A failed fetch drops that device (with a warning) and is not
 * cached, so a later query can still recover.
 */
function loadDeviceProducts() {
  if (DEVICE_PRODUCTS) return DEVICE_PRODUCTS

  const handles = config().deviceHandles
  let failed = false

  DEVICE_PRODUCTS = Promise.all(
    DEVICE_ORDER.map(async (key) => {
      const handle = handles[key]
      if (!handle) {
        console.warn(
          `header-search: no product handle configured for device "${key}"`
        )
        failed = true
        return [key, null]
      }
      try {
        const product = await fetchJson(
          `${routeRoot()}products/${encodeURIComponent(handle)}.js`
        )
        return [
          key,
          {
            title: product.title || "",
            url: product.url || `${routeRoot()}products/${handle}`,
            image: thumbUrl(product.featured_image),
          },
        ]
      } catch (error) {
        console.warn(
          `header-search: could not load device product "${handle}"`,
          error
        )
        failed = true
        return [key, null]
      }
    })
  ).then((entries) => {
    if (failed) DEVICE_PRODUCTS = null
    return Object.fromEntries(entries)
  })

  return DEVICE_PRODUCTS
}

/**
 * The devices that can run something in the displayed frequency rows: the union
 * of their `devices` lists, deduped, cheapest first. Each row cites the
 * frequency it is answering — the first displayed row granting that device,
 * which is the
 * tightest-window match for a numeric query and the API's first hit for a text
 * one.
 */
function deviceRows(frequencies, devices) {
  const matched = new Map()

  frequencies.forEach((frequency) => {
    if (!frequency.devices) {
      // An API data problem, not something to guess at: no list, no row.
      console.warn(`header-search: no devices list on "${frequency.id}"`)
      return
    }
    // An empty list is legitimate (no device can play it). Ids the theme has
    // no product for are skipped by the DEVICE_ORDER filter below.
    frequency.devices.forEach((key) => {
      if (key && !matched.has(key)) matched.set(key, frequency.hz)
    })
  })

  return DEVICE_ORDER.filter((key) => matched.has(key) && devices[key])
    .slice(0, MAX_DEVICE_ROWS)
    .map((key) => ({
      title: devices[key].title,
      url: devices[key].url,
      image: devices[key].image,
      sub: `Runs ${formatHz(matched.get(key))} Hz`,
    }))
}

/**
 * Resolve a plan into the two groups. Both sources go out together and a
 * failure in one never suppresses the other. `complete` is false when anything
 * failed, which keeps a half-answer out of the cache.
 */
async function fetchResults(plan, signal) {
  const frequencyRequest = plan.requests.find(
    (request) => request.source === "frequencies"
  )
  const productRequest = plan.requests.find(
    (request) => request.source === "products"
  )

  const settle = async (label, run) => {
    try {
      return { ok: true, value: await run() }
    } catch (error) {
      if (isAbort(error)) return { ok: false, aborted: true, value: null }
      console.warn(`header-search: ${label} failed`, error)
      return { ok: false, value: null }
    }
  }

  const [frequencyOutcome, productOutcome] = await Promise.all([
    frequencyRequest
      ? settle("frequency search", () =>
          fetchFrequencies(frequencyRequest, signal)
        )
      : { ok: true, value: [] },
    productRequest
      ? settle("product search", () => fetchProducts(productRequest, signal))
      : plan.kind === "numeric" && frequencyRequest
        ? settle("device products", () => loadDeviceProducts())
        : { ok: true, value: null },
  ])

  const frequencies = frequencyOutcome.value || []
  let products = []
  if (productRequest) {
    products = productOutcome.value || []
  } else if (plan.kind === "numeric" && productOutcome.value) {
    products = deviceRows(frequencies, productOutcome.value)
  }

  // A TEXT query that matched frequencies but no products still has a device
  // answer to give: the devices that can run what it matched. It fills the
  // otherwise-empty Products group rather than sitting alongside product hits,
  // so a query that did match products is left exactly as it was. Fetched here
  // instead of in the parallel pass above because it depends on the product
  // result being empty; loadDeviceProducts caches for the page, so this is at
  // most one extra round trip per session.
  let deviceOk = true
  if (plan.kind === "text" && !products.length && frequencies.length) {
    const deviceOutcome = await settle("device products", () =>
      loadDeviceProducts()
    )
    deviceOk = deviceOutcome.ok
    if (deviceOutcome.value) {
      products = deviceRows(frequencies, deviceOutcome.value)
    }
  }

  return {
    complete: frequencyOutcome.ok && productOutcome.ok && deviceOk,
    results: { products, frequencies },
  }
}

// ---- Formatting -----------------------------------------------------------

function escapeHtml(value) {
  return String(value == null ? "" : value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
}

function escapeRegExp(value) {
  return String(value).replace(/[.*+?^${}()|[\]\\]/g, "\\$&")
}

/** Escape for HTML, then bold what the customer typed inside the result. */
function highlight(value, term) {
  const safe = escapeHtml(value)
  if (!term) return safe
  return safe.replace(
    new RegExp("(" + escapeRegExp(escapeHtml(term)) + ")", "gi"),
    "<strong>$1</strong>"
  )
}

/** Two decimals at most, trailing zeros dropped, thousands grouped. */
function formatHz(hz) {
  // Idle rows carry their Hz as a string from Liquid; results carry a number.
  const rounded = Math.round(Number(hz) * 100) / 100
  const [whole, decimals] = rounded.toFixed(2).split(".")
  const grouped = whole.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
  const tail = decimals.replace(/0+$/, "")
  return tail ? `${grouped}.${tail}` : grouped
}

let PRICE_FORMAT = null

/** Predictive search reports price as a decimal string ("249.99"). */
function formatPrice(price) {
  const amount = parseFloat(price)
  if (!isFinite(amount)) return ""
  if (!PRICE_FORMAT) {
    const currency =
      (window.Shopify &&
        window.Shopify.currency &&
        window.Shopify.currency.active) ||
      "USD"
    try {
      PRICE_FORMAT = new Intl.NumberFormat(undefined, {
        style: "currency",
        currency,
      })
    } catch (error) {
      PRICE_FORMAT = { format: (value) => "$" + value.toFixed(2) }
    }
  }
  return PRICE_FORMAT.format(amount)
}

/** Ask the Shopify CDN for a thumbnail-sized copy. */
function thumbUrl(url) {
  if (!url) return ""
  const absolute = url.startsWith("//") ? "https:" + url : url
  try {
    const parsed = new URL(absolute, window.location.origin)
    parsed.searchParams.set("width", "112")
    return parsed.toString()
  } catch (error) {
    return absolute
  }
}

/**
 * Frequency rows deep-link into the library's detail view. The parameter is the
 * library's canonical Hz form (it rewrites the URL to that form on first
 * interaction, so an id would make the URL churn). The search terms ride along
 * for TEXT queries only — appending them to a numeric query would leave the
 * catalog behind the detail view apparently empty, because the library's own
 * filter matches differently than the Hz windows do.
 */
function frequencyUrl(frequency, plan) {
  const params = new URLSearchParams({ f: String(frequency.hz) })
  if (plan.kind === "text") params.set("q", plan.term)
  return `${config().libraryUrl}?${params}`
}

// ---- Rendering ------------------------------------------------------------

function productRowHtml(product, term) {
  const thumb = product.image
    ? `<img class="nav-thumb" src="${escapeHtml(product.image)}" width="56" height="56" loading="lazy" alt="${escapeHtml(product.title)}">`
    : '<span class="nav-thumb nav-thumb-empty"></span>'

  return (
    `<a class="nav-row nav-row-media" data-search-row href="${escapeHtml(product.url)}">` +
    thumb +
    '<span class="nav-row-text">' +
    `<span class="nav-row-title">${highlight(product.title, term)}</span>` +
    (product.sub
      ? `<span class="nav-row-sub">${highlight(product.sub, term)}</span>`
      : "") +
    "</span>" +
    CARET_RIGHT +
    "</a>"
  )
}

function frequencyRowHtml(frequency, url, term) {
  return (
    `<a class="nav-freq-row" data-search-row href="${escapeHtml(url)}">` +
    `<span class="nav-freq-hz">${highlight(formatHz(frequency.hz) + " Hz", term)}</span>` +
    `<span class="nav-freq-label">${highlight(frequency.name, term)}</span>` +
    CARET_RIGHT +
    "</a>"
  )
}

/**
 * The empty state. A query that matched nothing is the one moment search has no
 * answer of its own, so it hands the customer to Schummi with what they typed
 * already in the question — the chat page reads `?q=` and prefills its composer
 * (see sections/schummi-chat-ai-page.liquid). It carries `data-search-row`, so
 * arrow-keys-then-Enter reaches it like any result.
 */
function emptyStateHtml(term) {
  const quoted = `&ldquo;${escapeHtml(term)}&rdquo;`
  const url = `${config().schummiUrl}?q=${encodeURIComponent(term)}`

  return (
    '<div class="nav-search-empty-state">' +
    `<p class="nav-search-empty">No matches for ${quoted}. ` +
    "Try a feeling (&ldquo;sleep&rdquo;, &ldquo;focus&rdquo;) or a frequency (&ldquo;7.83&rdquo;).</p>" +
    `<a class="nav-search-ask" data-search-row href="${escapeHtml(url)}">` +
    `<span class="nav-sparkle">${SPARKLE}</span>` +
    '<span class="nav-row-text">' +
    `<span class="nav-row-title">Ask SchummiAi about ${quoted}</span>` +
    '<span class="nav-row-sub">Our AI guide answers specific frequency questions.</span>' +
    "</span>" +
    CARET_RIGHT +
    "</a>" +
    "</div>"
  )
}

function groupHtml(label, rows) {
  if (!rows.length) return ""
  return `<div class="nav-search-group"><p class="nav-eyebrow">${label}</p>${rows.join("")}</div>`
}

class HeaderSearch extends HTMLElement {
  connectedCallback() {
    this.surface = this.dataset.surface || "desktop"
    this.input = this.querySelector("[data-search-input]")
    this.results = this.querySelector("[data-search-results]")
    this.status = this.querySelector("[data-search-status]")
    if (!this.input || !this.results) return

    this.timer = null
    this.controller = null
    this.queryId = 0
    this.rows = []
    this.activeRow = -1

    this.onInputBound = this.onInput.bind(this)
    this.input.addEventListener("input", this.onInputBound)
    // Fires when the customer hits the native clear or Escape in a search field.
    this.input.addEventListener("search", this.onInputBound)
    this.addEventListener("keydown", this.onKeydown.bind(this))

    const clear = this.querySelector("[data-search-clear]")
    if (clear) {
      clear.addEventListener("click", () => {
        this.reset()
        this.input.focus()
      })
    }

    // The idle panel opens instantly: these rows come from Liquid, so nothing
    // is fetched until something is typed.
    this.renderIdle()
  }

  disconnectedCallback() {
    clearTimeout(this.timer)
    if (this.controller) this.controller.abort()
  }

  /** Clear the query and go back to the idle rows. Called when a panel closes. */
  reset() {
    if (!this.input) return
    this.input.value = ""
    this.onInput()
  }

  focusInput() {
    if (this.input) this.input.focus()
  }

  onInput() {
    const plan = planQuery(this.input.value)

    // Everything cheap happens on the keystroke: the clear button, and the
    // drawer swapping its menu for results. Only the network is debounced.
    const clear = this.querySelector("[data-search-clear]")
    if (clear) clear.hidden = this.input.value.trim() === ""
    this.dispatchEvent(
      new CustomEvent("header-search:query", {
        bubbles: true,
        detail: {
          surface: this.surface,
          hasQuery: plan.kind === "text" || plan.kind === "numeric",
        },
      })
    )

    // Whatever was in flight answers a query the customer has moved past.
    clearTimeout(this.timer)
    if (this.controller) this.controller.abort()

    if (plan.kind === "idle" || plan.kind === "short") {
      this.queryId += 1
      this.renderIdle()
      return
    }
    this.timer = setTimeout(() => this.run(plan), DEBOUNCE_MS)
  }

  async run(plan) {
    const id = (this.queryId += 1)
    if (this.controller) this.controller.abort()

    const key = `${plan.kind}|${plan.term}`
    const cached = RESULT_CACHE.get(key)
    if (cached) {
      this.renderResults(plan, cached, true)
      return
    }

    const controller = new AbortController()
    this.controller = controller
    // Previous results stay on screen while this is in flight, so the panel
    // doesn't blank on every character.
    this.results.setAttribute("data-loading", "")

    const outcome = await fetchResults(plan, controller.signal)
    // A newer keystroke has already taken over; its own render owns the panel.
    if (controller.signal.aborted || id !== this.queryId) return

    if (outcome.complete) RESULT_CACHE.set(key, outcome.results)
    this.renderResults(plan, outcome.results, outcome.complete)
  }

  renderIdle() {
    const idle = config().idle
    // The drawer shows its menu when there's no query, so it has no idle rows.
    if (this.surface === "mobile") {
      this.paint("")
      this.announce("")
      return
    }

    const html =
      groupHtml(
        "Products",
        // Idle rows carry their handle, not their copy: the subtitle map is the
        // one place that copy lives, idle and result rows alike.
        idle.products.map((product) =>
          productRowHtml(
            { ...product, sub: config().subtitles[product.handle] || "" },
            ""
          )
        )
      ) +
      groupHtml(
        "Frequency Library",
        idle.frequencies.map((frequency) =>
          frequencyRowHtml(
            frequency,
            `${config().libraryUrl}?f=${encodeURIComponent(frequency.hz)}`,
            ""
          )
        )
      )

    this.paint(html)
    this.announce("")
  }

  renderResults(plan, results, complete) {
    const term = plan.kind === "text" ? plan.term : ""
    const html =
      groupHtml(
        "Products",
        results.products.map((product) => productRowHtml(product, term))
      ) +
      groupHtml(
        "Frequency Library",
        results.frequencies.map((frequency) =>
          frequencyRowHtml(frequency, frequencyUrl(frequency, plan), term)
        )
      )

    const count = results.products.length + results.frequencies.length
    if (html) {
      this.paint(html)
      this.announce(`${count} result${count === 1 ? "" : "s"} for ${plan.term}`)
    } else if (complete) {
      // A query that genuinely matched nothing says so, suggests what to try,
      // and offers Schummi as the way to ask the question instead.
      this.paint(emptyStateHtml(plan.term))
      this.announce(`No results for ${plan.term}. Ask SchummiAi about it.`)
    } else {
      // A failed request degrades to nothing — an error box in the navbar looks
      // like a broken site. The console has the details.
      this.paint("")
      this.announce("")
    }
  }

  paint(html) {
    this.results.removeAttribute("data-loading")
    this.results.innerHTML = html
    this.rows = Array.from(this.results.querySelectorAll("[data-search-row]"))
    this.activeRow = -1
  }

  announce(message) {
    if (this.status) this.status.textContent = message
  }

  onKeydown(event) {
    if (event.key === "ArrowDown") {
      event.preventDefault()
      this.moveActiveRow(1)
    } else if (event.key === "ArrowUp") {
      event.preventDefault()
      this.moveActiveRow(-1)
    } else if (event.key === "Enter") {
      const row = this.rows[this.activeRow]
      // Nothing highlighted: let the form submit to the full search page.
      if (!row) return
      event.preventDefault()
      row.click()
    }
  }

  moveActiveRow(step) {
    if (!this.rows.length) return
    const next = this.activeRow + step

    this.rows.forEach((row) => row.removeAttribute("data-highlighted"))
    if (next < 0) {
      this.activeRow = -1
      this.focusInput()
      return
    }

    this.activeRow = Math.min(next, this.rows.length - 1)
    const row = this.rows[this.activeRow]
    row.setAttribute("data-highlighted", "")
    row.scrollIntoView({ block: "nearest" })
  }
}

customElements.define("header-search", HeaderSearch)
